import { CartLinesUpdateEvent } from '@shopify/events';

/* Bundle and complementary-book forms share the theme's native cart and drawer. */
if (!customElements.get('bf-commerce-form')) {
  class BfCommerceForm extends HTMLElement {
    static pending = false;

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('submit', this.submit, { signal: this.controller.signal });
      this.addEventListener('click', this.checkCart, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    get drawer() {
      return document.querySelector('theme-drawer#cart-drawer');
    }

    get sectionIds() {
      return [...new Set([...document.querySelectorAll('cart-items-component')]
        .map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    }

    async readJSON(url, signal) {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal,
      });
      if (!response.ok) throw new Error('Cart could not be refreshed.');
      return response.json();
    }

    async refreshDrawer(cartURL, signal, sections, items = []) {
      const drawer = this.drawer;
      if (!drawer) return false;

      const cartEndpoint = new URL(cartURL);
      cartEndpoint.pathname = cartEndpoint.pathname.replace(/\/$/, '') + '.js';
      const cart = await this.readJSON(cartEndpoint.href, signal);
      if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) {
        throw new Error('Cart response is incomplete.');
      }

      const sectionIds = this.sectionIds;
      if (sectionIds.some(id => typeof sections?.[id] !== 'string' || !sections[id])) {
        const sectionURL = new URL(window.location.href);
        sectionURL.searchParams.set('sections', sectionIds.join(','));
        sections = await this.readJSON(sectionURL.href, signal);
      }
      if (!sectionIds.length || sectionIds.some(id => !sections?.[id])) {
        throw new Error('Cart sections are unavailable.');
      }

      // Wait for the existing components, never create or replace a drawer here.
      const ready = Promise.all(['theme-drawer', 'cart-items-component', 'cart-drawer-component']
        .map(name => customElements.whenDefined(name)));
      let onAbort;
      try {
        signal.throwIfAborted();
        await Promise.race([ready, new Promise((_, reject) => {
          onAbort = () => reject(signal.reason);
          signal.addEventListener('abort', onAbort, { once: true });
        })]);
      } finally {
        signal.removeEventListener('abort', onAbort);
      }

      // Submit buttons are temporarily disabled. Give the native drawer a stable
      // focus-return target, shared with opening it from the header.
      if (!drawer.isOpen) {
        document.querySelector('[aria-controls="cart-drawer"]')?.focus({ preventScroll: true });
      }
      const update = new CartLinesUpdateEvent({
        action: items.length ? 'add' : 'update',
        context: !items.length || this.closest('cart-items-component') ? 'cart' : 'product',
        lines: items.length
          ? items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity }))
          : cart.items.map(item => ({ id: String(item.key || item.id), quantity: item.quantity })),
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections, source: 'bf-commerce-form', didError: false },
        }),
      });
      // Hydration can remove an upsell form. Dispatch from the stable document so
      // the native items, header badge and drawer receive the same absolute cart.
      document.dispatchEvent(update);
      await update.promise;
      if (!drawer.isOpen) drawer.open();
      return true;
    }

    checkCart = async (event) => {
      const link = event.target.closest?.('[data-commerce-cart-link]');
      if (!link || !this.contains(link) || !this.drawer) return;
      event.preventDefault();
      if (BfCommerceForm.pending) return;
      const cartURL = new URL(this.dataset.cartUrl, window.location.href);
      if (cartURL.origin !== window.location.origin) return;
      BfCommerceForm.pending = true;
      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      const status = this.querySelector('[data-commerce-status]');
      if (status) status.textContent = 'Dein Warenkorb wird aktualisiert …';
      try {
        // Verification only reads the cart. An uncertain add is never repeated.
        await this.refreshDrawer(cartURL, request.signal);
        if (status) status.textContent = 'Bitte prüfe deine Bücher im Warenkorb.';
      } catch {
        if (status) status.textContent = 'Der Warenkorb konnte gerade nicht geladen werden. Bitte versuche „Warenkorb prüfen“ erneut.';
      } finally {
        clearTimeout(timeout);
        BfCommerceForm.pending = false;
      }
    };

    submit = async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      event.preventDefault();
      if (BfCommerceForm.pending || this.hasAttribute('data-check-cart')) return;

      const status = this.querySelector('[data-commerce-status]');
      const cartLink = this.querySelector('[data-commerce-cart-link]');
      const action = new URL(form.action, window.location.href);
      const cartURL = new URL(this.dataset.cartUrl, window.location.href);
      if (action.origin !== window.location.origin || cartURL.origin !== window.location.origin) return;

      const data = new FormData(form);
      const selection = [];
      for (let i = 0; data.has(`items[${i}][id]`); i++) {
        selection.push({ id: Number(data.get(`items[${i}][id]`)), quantity: Number(data.get(`items[${i}][quantity]`)) });
      }
      if (!selection.length || selection.some(({ id, quantity }) => !Number.isSafeInteger(id) || id < 1 || quantity !== 1)) {
        if (status) status.textContent = 'Bitte lade die Seite neu, um die aktuelle Buchauswahl zu sehen.';
        return;
      }

      const quantities = new Map();
      selection.forEach(({ id, quantity }) => quantities.set(id, (quantities.get(id) || 0) + quantity));
      const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
      BfCommerceForm.pending = true;
      const buttons = [...document.querySelectorAll('bf-commerce-form button[type="submit"]')];
      const previousStates = buttons.map(button => button.disabled);
      buttons.forEach(button => { button.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      if (cartLink) cartLink.hidden = true;
      if (status) status.textContent = selection.length > 1 ? `${selection.length} Bücher werden hinzugefügt …` : 'Das Buch wird hinzugefügt …';

      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      let added = false;
      try {
        action.pathname = action.pathname.replace(/\/add\/?$/, '/add.js');
        const payload = { items };
        if (this.drawer) {
          payload.sections = this.sectionIds;
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(action.href, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
          signal: request.signal,
        });
        const result = await response.json();
        if (!response.ok || !Array.isArray(result.items) || items.some(item => !result.items.some(line => Number(line.variant_id || line.id) === item.id))) {
          throw new Error('Cart response could not confirm every book.');
        }
        added = true;
        if (status) status.textContent = 'Hinzugefügt. Dein Warenkorb wird geöffnet …';
        if (!await this.refreshDrawer(cartURL, request.signal, result.sections, items)) {
          window.location.assign(cartURL.href);
        }
      } catch {
        // A failed response can still follow a server-side add. Never retry it automatically.
        this.setAttribute('data-check-cart', '');
        if (status) {
          status.textContent = added
            ? 'Deine Bücher wurden hinzugefügt. Bitte aktualisiere die Ansicht über „Warenkorb prüfen“.'
            : 'Die Auswahl konnte nicht vollständig bestätigt werden. Bitte prüfe deinen Warenkorb, bevor du erneut etwas hinzufügst.';
          status.setAttribute('tabindex', '-1');
          status.focus();
        }
        if (cartLink) cartLink.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.removeAttribute('aria-busy');
        BfCommerceForm.pending = false;
        buttons.forEach((button, index) => {
          button.disabled = previousStates[index] || (this.hasAttribute('data-check-cart') && this.contains(button));
        });
      }
    };
  }

  customElements.define('bf-commerce-form', BfCommerceForm);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    BfCommerceForm.pending = false;
    document.querySelectorAll('bf-commerce-form').forEach(form => {
      form.removeAttribute('aria-busy');
      if (form.hasAttribute('data-check-cart')) return;
      form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = false; });
      const status = form.querySelector('[data-commerce-status]');
      if (status) status.textContent = '';
    });
  });
}
